
import Link from '@/components/compat/Link';
import { useRouter } from '@/lib/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeft,
  BookOpen,
  ExternalLink,
  Frame,
  GripVertical,
  Info,
  Palette,
  LayoutGrid,
  ScrollText,
  SearchCode,
  Sticker,
} from 'lucide-react';
import { PawnNavIcon, SmileNavIcon } from '@/components/help-center/MaskedSvgIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { fetchPowerInfo, formatFairtradePrice, getPowerCategoryLabel, MOCK_POWERS } from '@/lib/powers-data';
import { hasFairtradePrice, applyFairtradePrices } from '@/lib/fairtrade-data';
import { PowerPstyleEditor } from '@/components/painel/powers/PowerPstyleEditor';
import { EntitySeoSection } from '@/components/painel/seo/EntitySeoSection';
import {
  emptyPowerPstyleEditorInput,
  powerToPstyleEditorInput,
} from '@/lib/power-pstyle-cms';
import {
  apiPowerToEditorPatch,
  editorInputToPowerPreview,
  emptyPowerTutorial,
  powerToEditorInput,
  slugifyPowerName,
  type PowerEditorInput,
} from '@/lib/powers-cms';
import { CALLOUT_COLOR_IDS, getCalloutColor, type CalloutColorId } from '@/lib/callout-colors';
import { emptyRelatedExplore } from '@/lib/related-explore';
import { RelatedExploreEditorField } from '@/components/painel/cms/RelatedExploreEditorField';
import { painelSurfaceClass, painelSwitchClass } from '@/components/painel/painel-styles';
import { painelInputClass, painelTextareaClass } from '@/components/painel/cms/painel-form';
import { GuideBlocksEditor } from '@/components/painel/cms/GuideBlocksEditor';
import { AvatarFramesEditor } from '@/components/painel/cms/AvatarFramesEditor';
import { RichTextEditor } from '@/components/painel/cms/RichTextEditor';
import { SmilieCodesGrid } from '@/components/painel/cms/SmilieCodeField';
import { StickerCodesGrid } from '@/components/painel/cms/StickerCodeField';
import { PowerLoreEditor } from '@/components/painel/powers/PowerLoreEditor';
import { PowerPawnsEditor } from '@/components/painel/powers/PowerPawnsEditor';
import { PowerEditorHeroPreview } from '@/components/painel/powers/PowerEditorHeroPreview';
import { PowerEditorBasicInfoModal } from '@/components/painel/powers/PowerEditorBasicInfoModal';
import { PowerSectionSmileyIcon } from '@/components/help-center/powers/power-gallery-view';
import { usePowersCms, useFairtradePrices, usePowerWithFairtradePrices, useCatalogPowers } from '@/components/painel/powers/powers-cms-context';
import { usePainelSaveFeedback } from '@/components/painel/PainelSaveFeedback';
import { sessionHasPermission } from '@/lib/painel-rbac';
import { usePainelSession } from '@/components/painel/rbac/painel-session-context';
import { logPainelEditorPublicationActivity } from '@/lib/painel-activity-log';
import { resolvePublicationOnSave } from '@/lib/cms-publication';
import { emptyPowerLore, normalizePowerLore, powerLoreHasRenderableContent } from '@/lib/power-lore';
import { cn } from '@/lib/utils';
import { painelButtonPrimaryClass, painelButtonOutlineClass } from '@/components/painel/painel-styles';

type PainelPowerEditorPageProps =
  | { mode: 'new' }
  | { mode: 'edit'; powerSlug: string };

type EditorSectionId =
  | 'about'
  | 'callouts'
  | 'stickers'
  | 'smilies'
  | 'pawns'
  | 'lore'
  | 'tutorial'
  | 'avatarFrames'
  | 'pstyle'
  | 'seo';

const DEFAULT_EDITOR_SECTION_ORDER: EditorSectionId[] = [
  'about',
  'callouts',
  'lore',
  'smilies',
  'pawns',
  'stickers',
  'tutorial',
  'avatarFrames',
  'pstyle',
  'seo',
];

const emptyInput = (): PowerEditorInput => ({
  name: '',
  slug: '',
  id: 0,
  category: 'smiley',
  limited: false,
  released: new Date().toISOString().slice(0, 10),
  storePrice: '0',
  fairtradeMin: 0,
  fairtradeMax: 0,
  descriptionShort: '',
  descriptionLong: '',
  overviewSmiley: 'read',
  overviewSubtitle: 'Documentação',
  overviewTitle: 'Sobre este power',
  smilies: [],
  stickers: [],
  pawns: [],
  tutorialEnabled: false,
  tutorial: emptyPowerTutorial(),
  pstyle: emptyPowerPstyleEditorInput(),
  avatarFramesEnabled: false,
  avatarFrames: [],
  docSectionsEnabled: false,
  docSections: [],
  lore: emptyPowerLore(),
  calloutHowItWorks: '',
  calloutHowItWorksTitle: '',
  calloutHowItWorksColor: 'info',
  calloutLimited: '',
  calloutLimitedTitle: '',
  calloutLimitedColor: 'warning',
  relatedExplore: emptyRelatedExplore(),
});

const API_FETCH_DEBOUNCE_MS = 650;

function SectionCard({
  title,
  description,
  icon: Icon,
  accentClass = 'bg-sky-500/15 text-sky-300 ring-sky-400/25',
  children,
  draggable,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDragging,
  isDropTarget,
}: {
  title: string;
  description?: string;
  icon: LucideIcon;
  accentClass?: string;
  children: React.ReactNode;
  draggable?: boolean;
  onDragStart?: () => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: () => void;
  onDragEnd?: () => void;
  isDragging?: boolean;
  isDropTarget?: boolean;
}) {
  return (
    <div
      className={cn(
        painelSurfaceClass,
        'overflow-hidden p-0 transition-all',
        isDragging && 'opacity-55',
        isDropTarget && 'ring-2 ring-sky-400/40'
      )}
      onDragOver={
        draggable
          ? (e) => {
              e.preventDefault();
              onDragOver?.(e);
            }
          : undefined
      }
      onDrop={
        draggable
          ? (e) => {
              e.preventDefault();
              onDrop?.();
            }
          : undefined
      }
    >
      <div className="flex items-start gap-3 border-b border-white/10 bg-gradient-to-r from-white/[0.04] to-transparent px-5 py-4 md:gap-4 md:px-6">
        {draggable ? (
          <button
            type="button"
            draggable
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = 'move';
              onDragStart?.();
            }}
            onDragEnd={onDragEnd}
            className="mt-1 inline-flex h-9 w-9 shrink-0 cursor-grab items-center justify-center rounded-lg text-slate-500 hover:bg-white/10 hover:text-sky-200 active:cursor-grabbing"
            aria-label={`Arrastar secção ${title}`}
            title="Arrastar para reordenar"
          >
            <GripVertical className="h-5 w-5" />
          </button>
        ) : null}
        <span
          className={cn(
            'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-1',
            accentClass
          )}
        >
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-poppins text-lg font-semibold text-white">{title}</h2>
          {description ? (
            <p className="mt-1 font-inter text-sm leading-relaxed text-slate-400">{description}</p>
          ) : null}
        </div>
      </div>
      <div className="space-y-4 p-5 md:p-6">{children}</div>
    </div>
  );
}

function CalloutEditorBlock({
  label,
  defaultTitle,
  title,
  color,
  content,
  resetKey,
  placeholder,
  disabled,
  onTitleChange,
  onColorChange,
  onContentChange,
}: {
  label: string;
  defaultTitle: string;
  title: string;
  color: CalloutColorId;
  content: string;
  resetKey: string;
  placeholder?: string;
  disabled?: boolean;
  onTitleChange: (v: string) => void;
  onColorChange: (v: CalloutColorId) => void;
  onContentChange: (v: string) => void;
}) {
  const style = getCalloutColor(color);
  return (
    <div
      className={cn('space-y-3 rounded-xl border p-4', disabled && 'pointer-events-none opacity-50')}
      style={{
        backgroundColor: `${style.swatch}14`,
        borderColor: `${style.swatch}55`,
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-poppins text-xs font-semibold uppercase tracking-wide text-slate-400">
          {label}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {CALLOUT_COLOR_IDS.map((id) => {
            const c = getCalloutColor(id);
            const active = color === id;
            return (
              <button
                key={id}
                type="button"
                title={c.label}
                aria-label={c.label}
                aria-pressed={active}
                onClick={() => onColorChange(id)}
                className={cn(
                  'h-6 w-6 rounded-full ring-2 transition-transform',
                  active ? 'scale-110 ring-white' : 'ring-transparent hover:scale-105'
                )}
                style={{ backgroundColor: c.swatch }}
              />
            );
          })}
        </div>
      </div>
      <div className="space-y-2">
        <Label className="font-poppins text-slate-300">Título</Label>
        <Input
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          className={painelInputClass}
          placeholder={defaultTitle}
          disabled={disabled}
        />
      </div>
      <div className="space-y-2">
        <Label className="font-poppins text-slate-300">Conteúdo</Label>
        <RichTextEditor
          value={content}
          onChange={onContentChange}
          toolbar="extended"
          tone={style.editorTone}
          editorMinHeightClass="min-h-[140px]"
          resetKey={resetKey}
          disabled={disabled}
          className={disabled ? 'pointer-events-none opacity-60' : undefined}
        />
        {placeholder && !content.trim() ? (
          <p className="font-inter text-xs text-slate-500">{placeholder}</p>
        ) : null}
      </div>
    </div>
  );
}

export function PainelPowerEditorPage(props: PainelPowerEditorPageProps) {
  const router = useRouter();
  const { session, role, hydrated: sessionHydrated } = usePainelSession();
  const { showSaveSuccess } = usePainelSaveFeedback();
  const { hydrated, addPower, updatePower, getPowerBySlug, powers } = usePowersCms();
  const { powers: catalogPowers } = useCatalogPowers();
  const fairtrade = useFairtradePrices();
  const exploreCatalog = useMemo(() => {
    const bySlug = new Map<string, (typeof MOCK_POWERS)[number]>();
    for (const power of MOCK_POWERS) bySlug.set(power.slug.toLowerCase(), power);
    for (const power of catalogPowers) bySlug.set(power.slug.toLowerCase(), power);
    for (const power of powers) bySlug.set(power.slug.toLowerCase(), power);
    return Array.from(bySlug.values());
  }, [catalogPowers, powers]);
  const [form, setForm] = useState<PowerEditorInput>(emptyInput);
  const [slugTouched, setSlugTouched] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const [basicsUnlocked, setBasicsUnlocked] = useState(props.mode === 'edit');
  /** Em modo «novo», só consulta a API depois de o utilizador confirmar o nome. */
  const [nameConfirmed, setNameConfirmed] = useState(props.mode === 'edit');
  const [basicInfoOpen, setBasicInfoOpen] = useState(false);
  const [sectionOrder, setSectionOrder] = useState<EditorSectionId[]>(DEFAULT_EDITOR_SECTION_ORDER);
  const [draggingSectionId, setDraggingSectionId] = useState<EditorSectionId | null>(null);
  const [dropTargetSectionId, setDropTargetSectionId] = useState<EditorSectionId | null>(null);
  const lastFetchedSlugRef = useRef<string | null>(null);
  const fetchRequestRef = useRef(0);
  const seededSlugRef = useRef<string | null>(null);

  useEffect(() => {
    if (!sessionHydrated) return;
    const allowed =
      props.mode === 'new'
        ? sessionHasPermission(session, 'powers.create')
        : sessionHasPermission(session, 'powers.edit');
    if (!allowed) {
      router.replace('/painel/powers');
    }
  }, [sessionHydrated, session, props.mode, router]);

  useEffect(() => {
    if (!hydrated || props.mode !== 'edit') return;
    if (seededSlugRef.current === props.powerSlug) return;
    const row = getPowerBySlug(props.powerSlug);
    if (row) {
      const input = powerToEditorInput(row);
      setForm(input);
      setSlugTouched(true);
      seededSlugRef.current = props.powerSlug;

      // Persistir Lore do seed (Topman) / migração wiki → campo lore (só Super Admin).
      const hadStoredLore = powerLoreHasRenderableContent(normalizePowerLore(row.lore));
      const hasLoreNow = powerLoreHasRenderableContent(input.lore);
      const docsRemoved = (row.docSections?.length ?? 0) > (input.docSections?.length ?? 0);
      const loreNeedsPersist =
        role === 'super_admin' &&
        hasLoreNow &&
        (!hadStoredLore ||
          (row.slug.toLowerCase() === 'topman' && (row.lore?.curiosities?.length ?? 0) === 0));
      if (loreNeedsPersist || docsRemoved) {
        void updatePower(props.powerSlug, input).catch(() => {
          /* offline / API: a página pública já resolve via seed/migração em memória */
        });
      }
    }
  }, [hydrated, props, getPowerBySlug, updatePower, role]);

  /** Preenche stickers da API quando o CMS ainda não tem a lista guardada. */
  useEffect(() => {
    if (props.mode !== 'edit') return;
    const slug = form.slug.trim().toLowerCase();
    if (!slug || form.stickers.length > 0) return;

    let cancelled = false;
    void fetchPowerInfo(slug).then((data) => {
      if (cancelled || !data?.stickers?.length) return;
      setForm((prev) => {
        if (prev.stickers.length > 0) return prev;
        return {
          ...prev,
          stickers: [...data.stickers!],
          category:
            prev.category === 'smiley' || !prev.category.trim()
              ? 'stickers'
              : prev.category,
        };
      });
    });

    return () => {
      cancelled = true;
    };
  }, [props.mode, form.slug, form.stickers.length]);

  useEffect(() => {
    if (props.mode !== 'new') return;
    if (!nameConfirmed) {
      setApiLoading(false);
      return;
    }

    const slug = slugTouched ? form.slug.trim().toLowerCase() : slugifyPowerName(form.name);
    if (!slug || slug.length < 2) {
      setApiLoading(false);
      setApiMessage(null);
      return;
    }

    if (lastFetchedSlugRef.current === slug) return;

    const timer = window.setTimeout(async () => {
      const requestId = ++fetchRequestRef.current;
      setApiLoading(true);
      setApiMessage(null);

      try {
        const data = await fetchPowerInfo(slug);
        if (requestId !== fetchRequestRef.current) return;

        if (!data) {
          setApiMessage('Power não encontrado na API — preencha os campos manualmente.');
          lastFetchedSlugRef.current = slug;
          return;
        }

        const patch = apiPowerToEditorPatch(data);
        setForm((prev) => ({
          ...prev,
          ...patch,
          slug: slugTouched ? prev.slug : patch.slug,
          smilies: [...patch.smilies],
          stickers: [...(patch.stickers ?? [])],
          pawns: patch.pawns.map((p) => ({ ...p })),
          tutorialEnabled: prev.tutorialEnabled,
          tutorial: prev.tutorial,
          pstyle: prev.pstyle.customizeEnabled
            ? prev.pstyle
            : {
                ...prev.pstyle,
                config: powerToPstyleEditorInput(data).config,
              },
        }));
        lastFetchedSlugRef.current = slug;
        const pawnLabel =
          data.pawns.length === 1 ? '1 peão' : `${data.pawns.length} peões`;
        const smilieLabel =
          data.smilies.length === 1 ? '1 smiley' : `${data.smilies.length} smilies`;
        const stickerCount = data.stickers?.length ?? 0;
        const stickerLabel =
          stickerCount === 1 ? '1 sticker' : `${stickerCount} stickers`;
        setApiMessage(
          stickerCount > 0
            ? `Dados de “${data.name}” importados da API xatBlog (${stickerLabel}, ${smilieLabel}, ${pawnLabel}).`
            : `Dados de “${data.name}” importados da API xatBlog (${smilieLabel}, ${pawnLabel}).`
        );
      } catch {
        if (requestId === fetchRequestRef.current) {
          setApiMessage('Não foi possível consultar a API. Tente novamente ou preencha manualmente.');
        }
      } finally {
        if (requestId === fetchRequestRef.current) {
          setApiLoading(false);
          setBasicsUnlocked(true);
        }
      }
    }, basicsUnlocked ? API_FETCH_DEBOUNCE_MS : 0);

    return () => window.clearTimeout(timer);
  }, [props.mode, form.name, form.slug, slugTouched, nameConfirmed, basicsUnlocked]);

  const selectTriggerClass = cn(
    painelInputClass,
    'flex items-center justify-between text-left [&>span]:line-clamp-1'
  );

  const patch = (partial: Partial<PowerEditorInput>) => {
    setForm((prev) => {
      const next = { ...prev, ...partial };
      if (partial.name !== undefined && !slugTouched && props.mode === 'new') {
        const nextSlug = slugifyPowerName(partial.name);
        if (nextSlug !== lastFetchedSlugRef.current) {
          lastFetchedSlugRef.current = null;
        }
        next.slug = nextSlug;
      }
      if (partial.slug !== undefined && props.mode === 'new') {
        const key = partial.slug.trim().toLowerCase();
        if (key !== lastFetchedSlugRef.current) {
          lastFetchedSlugRef.current = null;
        }
      }
      return next;
    });
  };

  const handleSave = async () => {
    const name = form.name.trim();
    const slug = form.slug.trim().toLowerCase();
    if (!name || !slug) return;

    const apiPrices =
      fairtrade.prices && slug
        ? applyFairtradePrices(
            editorInputToPowerPreview({ ...form, name, slug }) ?? {
              id: form.id,
              name,
              slug,
              description: { short: form.descriptionShort, long: form.descriptionLong },
              prices: {
                store: String(form.storePrice),
                fairtrade_min: form.fairtradeMin,
                fairtrade_max: form.fairtradeMax,
              },
              status: form.limited ? 'limited' : false,
              released: form.released,
              category: form.category,
              type: form.category,
              limited: form.limited,
              smilies: form.smilies,
              pawns: form.pawns,
            },
            fairtrade.prices
          ).prices
        : null;

    const publicationStatus = resolvePublicationOnSave(role);
    const previousStatus =
      props.mode === 'edit' ? getPowerBySlug(props.powerSlug)?.publicationStatus ?? null : null;
    const payload: PowerEditorInput = {
      ...form,
      name,
      slug,
      fairtradeMin: apiPrices?.fairtrade_min ?? form.fairtradeMin,
      fairtradeMax: apiPrices?.fairtrade_max ?? form.fairtradeMax,
      publicationStatus,
      // Lore só pode ser alterada por Super Admin.
      lore:
        role === 'super_admin'
          ? form.lore
          : props.mode === 'edit'
            ? normalizePowerLore(getPowerBySlug(props.powerSlug)?.lore)
            : emptyPowerLore(),
    };

    try {
      if (props.mode === 'new') {
        if (powers.some((p) => p.slug === slug)) return;
        const created = await addPower(payload);
        logPainelEditorPublicationActivity(session, {
          resource: 'power',
          resourceId: created.slug,
          resourceLabel: name,
          isNew: true,
          previousStatus: null,
          nextStatus: publicationStatus,
        });
        showSaveSuccess({
          title: 'Power criado',
          description:
            publicationStatus === 'pending_review'
              ? `“${name}” foi enviado para revisão antes de ir ao ar.`
              : `“${name}” foi guardado e publicado no catálogo.`,
        });
        router.replace(`/painel/powers/${created.slug}/edit`);
      } else {
        await updatePower(props.powerSlug, payload);
        logPainelEditorPublicationActivity(session, {
          resource: 'power',
          resourceId: props.powerSlug,
          resourceLabel: name,
          isNew: false,
          previousStatus,
          nextStatus: publicationStatus,
        });
        showSaveSuccess({
          title: 'Power guardado',
          description:
            publicationStatus === 'pending_review'
              ? `“${name}” foi enviado para revisão antes de ir ao ar.`
              : `“${name}” foi guardado e publicado no catálogo.`,
        });
      }
    } catch {
      /* toast já emitido no CMS */
    }
  };

  const slugConflict =
    props.mode === 'new' &&
    form.slug.trim() &&
    powers.some((p) => p.slug === form.slug.trim().toLowerCase());

  const isEverypower = form.slug.trim().toLowerCase() === 'everypower';
  const slugKey = form.slug.trim().toLowerCase();
  const powerPreview = useMemo(() => editorInputToPowerPreview(form), [form]);
  const powerWithFairtrade = usePowerWithFairtradePrices(powerPreview);
  const resolvedFairtrade = powerWithFairtrade?.prices;
  const hasResolvedFairtrade = resolvedFairtrade
    ? hasFairtradePrice(resolvedFairtrade) ||
      resolvedFairtrade.fairtrade_min > 0 ||
      resolvedFairtrade.fairtrade_max > 0
    : false;
  const editorResetKey =
    props.mode === 'edit' ? `edit-${props.powerSlug}` : `new-${slugKey || 'draft'}`;

  const fairtradeMinLabel = !slugKey
    ? 'Defina o slug para consultar'
    : fairtrade.loading
      ? ''
      : hasResolvedFairtrade
        ? resolvedFairtrade!.fairtrade_min.toLocaleString('pt-BR')
        : '—';

  const fairtradeMaxLabel = !slugKey
    ? 'Defina o slug para consultar'
    : fairtrade.loading
      ? ''
      : hasResolvedFairtrade
        ? resolvedFairtrade!.fairtrade_max.toLocaleString('pt-BR')
        : '—';

  const fairtradeRangeLabel =
    !slugKey || fairtrade.loading
      ? ''
      : hasResolvedFairtrade
        ? formatFairtradePrice(resolvedFairtrade!.fairtrade_min, resolvedFairtrade!.fairtrade_max)
        : '—';

  const storeRaw = form.storePrice.trim();
  const storePriceLabel = apiLoading
    ? ''
    : !storeRaw
      ? props.mode === 'new' && !slugKey
        ? 'Defina o slug para consultar'
        : '—'
      : /^unknown$/i.test(storeRaw)
        ? 'Não comercializável'
        : storeRaw === '0'
          ? 'Gratuito'
          : (() => {
              const amount = storeRaw.replace(/\s*xats$/i, '').trim();
              const n = Number(amount.replace(/[^\d.-]/g, ''));
              return Number.isFinite(n) ? n.toLocaleString('pt-BR') : amount;
            })();

  const hasStorePrice =
    Boolean(storeRaw) &&
    storeRaw !== '0' &&
    !/^unknown$/i.test(storeRaw) &&
    storePriceLabel !== '—' &&
    storePriceLabel !== 'Gratuito' &&
    storePriceLabel !== 'Não comercializável' &&
    storePriceLabel !== 'Defina o slug para consultar';

  if (props.mode === 'edit' && !hydrated) {
    return <div className="font-inter text-sm text-slate-400">Carregando…</div>;
  }

  if (props.mode === 'edit' && hydrated && !getPowerBySlug(props.powerSlug)) {
    return (
      <div className="space-y-4">
        <h1 className="font-poppins text-2xl font-bold text-white">Power não encontrado</h1>
        <p className="text-sm text-slate-400">O slug não existe ou foi removido.</p>
        <Button asChild variant="outline" className="rounded-xl border-white/15 text-blue-100">
          <Link href="/painel/powers">Voltar aos powers</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-none space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <Button
            variant="ghost"
            asChild
            className="mb-2 -ml-2 rounded-xl text-slate-300 hover:bg-white/10 hover:text-white"
          >
            <Link href="/painel/powers">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Powers
            </Link>
          </Button>
          <h1 className="font-poppins text-2xl font-bold tracking-tight text-white md:text-3xl">
            {props.mode === 'new' ? 'Novo power' : `Editar ${form.name || 'power'}`}
          </h1>
          <p className="mt-1 font-inter text-sm text-slate-400">
            Metadados e conteúdo da página pública em /powers/[slug]. A data de lançamento aparece
            como DD/MM/AAAA no painel de informações. Para mencionar outro power com preview
            interativo, use o bbcode [[nomedopower]] (ou o botão «Mencionar power»).
            {isEverypower
              ? ' Inclui secções Everypower, tooltips de peões Esmeralda e temas Pstyle Fx.'
              : form.slug.trim().toLowerCase() === 'allpowers'
                ? ' Inclui a lista de powers necessários com filtros e paginação.'
                : '.'}
          </p>
        </div>
        {props.mode === 'edit' && form.slug ? (
          <Button
            type="button"
            variant="outline"
            asChild
            className="shrink-0 rounded-xl border-white/20 bg-white/[0.06] text-blue-100 hover:bg-white/10"
          >
            <Link href={`/powers/${form.slug}`} target="_blank">
              <ExternalLink className="mr-2 h-4 w-4" />
              Ver página
            </Link>
          </Button>
        ) : null}
      </div>

      <PowerEditorHeroPreview
        mode={props.mode}
        power={
          powerWithFairtrade ??
          (basicsUnlocked || props.mode === 'edit' ? powerPreview : null)
        }
        nameValue={form.name}
        onNameChange={(name) => patch({ name })}
        onConfirmName={() => {
          if (props.mode !== 'new' || nameConfirmed) return;
          const slug = slugifyPowerName(form.name);
          if (slug.length < 2 || slugConflict) return;
          lastFetchedSlugRef.current = null;
          setNameConfirmed(true);
        }}
        apiLoading={apiLoading}
        apiMessage={apiMessage}
        slugConflict={Boolean(slugConflict)}
        showEditButton={basicsUnlocked || props.mode === 'edit'}
        onEditClick={() => setBasicInfoOpen(true)}
      />

      <PowerEditorBasicInfoModal
        open={basicInfoOpen}
        onOpenChange={setBasicInfoOpen}
        form={form}
        mode={props.mode}
        slugConflict={Boolean(slugConflict)}
        slugTouched={slugTouched}
        onSlugTouched={() => setSlugTouched(true)}
        onPatch={patch}
        editorResetKey={editorResetKey}
        selectTriggerClass={selectTriggerClass}
        storePriceLabel={storePriceLabel}
        fairtradeMinLabel={fairtradeMinLabel}
        fairtradeMaxLabel={fairtradeMaxLabel}
        fairtradeRangeLabel={fairtradeRangeLabel}
        fairtradeUpdatedAt={fairtrade.updatedAt}
        apiLoading={apiLoading}
        fairtradeLoading={fairtrade.loading}
        slugKey={slugKey}
        hasStorePrice={hasStorePrice}
        hasResolvedFairtrade={hasResolvedFairtrade}
      />

      {sectionOrder.map((sectionId) => {
        const sortableProps = {
          draggable: true as const,
          onDragStart: () => setDraggingSectionId(sectionId),
          onDragOver: () => {
            if (draggingSectionId && draggingSectionId !== sectionId) {
              setDropTargetSectionId(sectionId);
            }
          },
          onDrop: () => {
            if (!draggingSectionId || draggingSectionId === sectionId) return;
            setSectionOrder((prev) => {
              const next = [...prev];
              const from = next.indexOf(draggingSectionId);
              const to = next.indexOf(sectionId);
              if (from < 0 || to < 0) return prev;
              next.splice(from, 1);
              next.splice(to, 0, draggingSectionId);
              return next;
            });
            setDraggingSectionId(null);
            setDropTargetSectionId(null);
          },
          onDragEnd: () => {
            setDraggingSectionId(null);
            setDropTargetSectionId(null);
          },
          isDragging: draggingSectionId === sectionId,
          isDropTarget: dropTargetSectionId === sectionId && draggingSectionId !== sectionId,
        };

        if (sectionId === 'about') {
          return (
            <SectionCard
              key={sectionId}
              {...sortableProps}
              icon={BookOpen}
              accentClass="bg-sky-500/15 text-sky-300 ring-sky-400/25"
              title="Sobre este power"
              description="Cabeçalho e descrição longa da secção pública abaixo do hero."
            >
              <div className="space-y-5">
                <div className="grid gap-4 sm:grid-cols-[auto_1fr]">
                  <div className="flex flex-col items-center gap-2">
                    <Label className="font-poppins text-slate-300">Smiley</Label>
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
                      <PowerSectionSmileyIcon
                        code={form.overviewSmiley.trim() || 'read'}
                        size="md"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-poppins text-slate-300" htmlFor="overview-smiley">
                      Código do smiley
                    </Label>
                    <Input
                      id="overview-smiley"
                      value={form.overviewSmiley}
                      onChange={(e) =>
                        patch({
                          overviewSmiley: e.target.value.replace(/[()]/g, '').trim(),
                        })
                      }
                      placeholder="read"
                      className={cn(painelInputClass, 'font-mono')}
                    />
                    <p className="font-inter text-xs text-slate-500">
                      Sem parênteses — ex.: <code className="text-sky-300/80">read</code>
                    </p>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="font-poppins text-slate-300" htmlFor="overview-subtitle">
                      Subtítulo
                    </Label>
                    <Input
                      id="overview-subtitle"
                      value={form.overviewSubtitle}
                      onChange={(e) => patch({ overviewSubtitle: e.target.value })}
                      placeholder="Documentação"
                      className={painelInputClass}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-poppins text-slate-300" htmlFor="overview-title">
                      Título
                    </Label>
                    <Input
                      id="overview-title"
                      value={form.overviewTitle}
                      onChange={(e) => patch({ overviewTitle: e.target.value })}
                      placeholder="Sobre este power"
                      className={painelInputClass}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="font-poppins text-slate-300">Descrição</Label>
                  <RichTextEditor
                    value={form.descriptionLong}
                    onChange={(descriptionLong) => patch({ descriptionLong })}
                    toolbar="extended"
                    editorMinHeightClass="min-h-[200px]"
                    resetKey={`${editorResetKey}-about-long`}
                  />
                </div>
              </div>
            </SectionCard>
          );
        }

        if (sectionId === 'callouts') {
          return (
            <SectionCard
              key={sectionId}
              {...sortableProps}
              icon={Info}
              accentClass="bg-blue-500/15 text-blue-300 ring-blue-400/25"
              title="Caixas de informação"
              description="Textos das caixas na página pública. Deixe o conteúdo em branco para o texto automático. Use a toolbar para menções e embeds ([[power]], [[smilies:]], [[pawns:]], [[ace:]])."
            >
              <CalloutEditorBlock
                label="Caixa principal"
                defaultTitle="Como funciona"
                title={form.calloutHowItWorksTitle}
                color={form.calloutHowItWorksColor}
                content={form.calloutHowItWorks}
                resetKey={`${editorResetKey}-callout-how`}
                placeholder={`Automático: O power ${form.name.trim() || '…'} é da categoria ${getPowerCategoryLabel(form.category)}.`}
                onTitleChange={(calloutHowItWorksTitle) => patch({ calloutHowItWorksTitle })}
                onColorChange={(calloutHowItWorksColor) => patch({ calloutHowItWorksColor })}
                onContentChange={(calloutHowItWorks) => patch({ calloutHowItWorks })}
              />
              <CalloutEditorBlock
                label="Caixa limitada"
                defaultTitle="Power Limitado"
                title={form.calloutLimitedTitle}
                color={form.calloutLimitedColor}
                content={form.calloutLimited}
                resetKey={`${editorResetKey}-callout-limited`}
                placeholder={`Automático: ${form.name.trim() || '…'} é uma edição especial e limitada…`}
                disabled={!form.limited}
                onTitleChange={(calloutLimitedTitle) => patch({ calloutLimitedTitle })}
                onColorChange={(calloutLimitedColor) => patch({ calloutLimitedColor })}
                onContentChange={(calloutLimited) => patch({ calloutLimited })}
              />
              {!form.limited ? (
                <p className="font-inter text-xs text-slate-500">
                  A caixa limitada só aparece na página pública quando o power está marcado como
                  limitado.
                </p>
              ) : null}
            </SectionCard>
          );
        }

        if (sectionId === 'stickers') {
          return (
            <SectionCard
              key={sectionId}
              {...sortableProps}
              icon={Sticker}
              accentClass="bg-pink-500/15 text-pink-300 ring-pink-400/25"
              title="Stickers"
              description="Códigos dos stickers deste power (vêm da API ao criar). A pré-visualização usa o CDN oficial."
            >
              <StickerCodesGrid
                powerSlug={form.slug || slugifyPowerName(form.name)}
                codes={form.stickers}
                onChange={(stickers) => patch({ stickers })}
                emptyHint="Ainda sem stickers — ao digitar o nome do power os códigos vêm da API, ou use o botão abaixo para adicionar."
              />
            </SectionCard>
          );
        }

        if (sectionId === 'smilies') {
          return (
            <SectionCard
              key={sectionId}
              {...sortableProps}
              icon={SmileNavIcon}
              accentClass="bg-amber-500/15 text-amber-300 ring-amber-400/25"
              title="Smilies"
              description={
                isEverypower
                  ? 'Everypower não usa smilies de power — a galeria pública mostra os peões Esmeralda.'
                  : 'Códigos dos smilies deste power (vêm da API ao criar). A pré-visualização actualiza ao editar cada código.'
              }
            >
              {!isEverypower ? (
                <SmilieCodesGrid
                  codes={form.smilies}
                  onChange={(smilies) => patch({ smilies })}
                  emptyHint="Ainda sem smilies — ao digitar o nome do power os códigos vêm da API, ou use o botão abaixo para adicionar."
                />
              ) : (
                <p className="font-inter text-sm text-slate-400">
                  Sem galeria de smilies neste power.
                </p>
              )}
            </SectionCard>
          );
        }

        if (sectionId === 'pawns') {
          return (
            <SectionCard
              key={sectionId}
              {...sortableProps}
              icon={PawnNavIcon}
              accentClass="bg-cyan-500/15 text-cyan-300 ring-cyan-400/25"
              title="Peões"
              description={
                isEverypower
                  ? 'Lista os 8 hats Esmeralda (Ehh, Ekh, …). Na página pública aparecem tooltips com peões de referência e modo claro/escuro.'
                  : 'Importados da API ao criar o power. Use o código hat e o nome interno do smiley (ex.: everypawnpink).'
              }
            >
              <PowerPawnsEditor value={form.pawns} onChange={(pawns) => patch({ pawns })} />
            </SectionCard>
          );
        }

        if (sectionId === 'lore') {
          const canEditLore = role === 'super_admin';
          return (
            <SectionCard
              key={sectionId}
              {...sortableProps}
              icon={ScrollText}
              accentClass="bg-cyan-500/15 text-cyan-300 ring-cyan-400/25"
              title="Lore e História"
              description="Experiência narrativa premium (smiley em destaque, capítulos e curiosidades). Opcional e separada da documentação técnica («Sobre este power»). Edição restrita a Super Admins."
            >
              <PowerLoreEditor
                lore={form.lore}
                onChange={(lore) => patch({ lore })}
                powerName={form.name}
                powerSlug={form.slug}
                defaultSmiley={form.smilies[0] || form.slug}
                suggestedSmilies={form.smilies}
                canEdit={canEditLore}
              />
            </SectionCard>
          );
        }

        if (sectionId === 'tutorial') {
          return (
            <SectionCard
              key={sectionId}
              {...sortableProps}
              icon={BookOpen}
              accentClass="bg-indigo-500/15 text-indigo-300 ring-indigo-400/25"
              title="Tutorial"
              description="Secção «Como usar» na página pública. Blocos rich text; mencione outros powers com [[nomedopower]] ou o botão «Mencionar power» na toolbar. Ative para substituir o guia genérico por categoria."
            >
              <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <Switch
                  id="tutorial-enabled"
                  checked={form.tutorialEnabled}
                  onCheckedChange={(tutorialEnabled) => patch({ tutorialEnabled })}
                  className={painelSwitchClass}
                />
                <Label htmlFor="tutorial-enabled" className="font-poppins text-slate-300">
                  Publicar tutorial personalizado neste power
                </Label>
              </div>

              {form.tutorialEnabled ? (
                <GuideBlocksEditor
                  value={form.tutorial}
                  onChange={(tutorial) => patch({ tutorial })}
                />
              ) : (
                <p className="font-inter text-sm text-slate-500">
                  Sem tutorial personalizado, a página usa o guia genérico por categoria (quando
                  aplicável).
                </p>
              )}
            </SectionCard>
          );
        }

        if (sectionId === 'avatarFrames') {
          return (
            <SectionCard
              key={sectionId}
              {...sortableProps}
              icon={Frame}
              accentClass="bg-rose-500/15 text-rose-300 ring-rose-400/25"
              title="Avatar frames"
              description="Secção de molduras na página pública. Desligada por padrão — envie cada imagem pelo painel quando activar."
            >
              <AvatarFramesEditor
                value={{
                  enabled: form.avatarFramesEnabled,
                  frames: form.avatarFrames,
                }}
                onChange={({ enabled, frames }) =>
                  patch({ avatarFramesEnabled: enabled, avatarFrames: frames })
                }
                description="Galeria igual à dos Aces (ex.: Vaf). Só aparece quando ativada."
              />
            </SectionCard>
          );
        }

        if (sectionId === 'seo') {
          if (!sessionHasPermission(session, 'seo.manage')) return null;
          return (
            <SectionCard
              key={sectionId}
              {...sortableProps}
              icon={SearchCode}
              accentClass="bg-sky-500/15 text-sky-300 ring-sky-400/25"
              title="SEO"
              description="Title, description, Open Graph e robots desta página de power."
            >
              <EntitySeoSection
                entityType="power"
                entityId={form.id > 0 ? form.id : null}
                enabled={props.mode === 'edit' && form.id > 0}
                fallbackTitle={form.name}
                fallbackDescription={form.descriptionShort}
                previewPath={form.slug ? `/powers/${form.slug}` : '/powers'}
              />
            </SectionCard>
          );
        }

        return (
          <SectionCard
            key={sectionId}
            {...sortableProps}
            icon={Palette}
            accentClass="bg-pink-500/15 text-pink-300 ring-pink-400/25"
            title="Pstyle"
            description={
              isEverypower
                ? 'Temas Emerald Fx e Supernova Fx com degradês animados na secção Everypower da página pública. A personalização abaixo aplica-se ao diálogo de perfil.'
                : 'Disponível em todos os powers — mesmo sem Pstyle hoje, para futuras atualizações. Ative a personalização para definir o tema do diálogo de perfil na página pública.'
            }
          >
            <PowerPstyleEditor
              value={form.pstyle}
              onChange={(pstyle) => patch({ pstyle })}
              avatarFallback={form.smilies[0] ?? form.slug}
            />
          </SectionCard>
        );
      })}

      <SectionCard
        icon={LayoutGrid}
        accentClass="bg-violet-500/15 text-violet-300 ring-violet-400/25"
        title="Explore mais"
        description="Secção fixa no final da página pública com até 4 powers sugeridos. O botão «Ver todos» continua a abrir a categoria deste power."
      >
        <RelatedExploreEditorField
          value={form.relatedExplore ?? emptyRelatedExplore()}
          onChange={(relatedExplore) => patch({ relatedExplore })}
          catalog={exploreCatalog}
          currentSlug={form.slug}
          defaultTitle={`Explore mais powers ${getPowerCategoryLabel(form.category)}`}
          itemLabel="Nome ou slug do power"
          cardLayout
          helpText="Os 4 slots espelham a página pública. Digite o nome do power em cada um — o card revela-se ao encontrar. Lista vazia = preenchimento automático da mesma categoria."
        />
      </SectionCard>

      <div className="flex flex-wrap gap-3">
        <Button
          type="button"
          onClick={handleSave}
          disabled={!form.name.trim() || !form.slug.trim() || slugConflict || apiLoading}
          className={painelButtonPrimaryClass}
        >
          Salvar power
        </Button>
        <Button
          type="button"
          variant="outline"
          asChild
          className={painelButtonOutlineClass}
        >
          <Link href="/painel/powers">Cancelar</Link>
        </Button>
      </div>
    </div>
  );
}
